import type { Scan, ScanReviewGroup } from "@hobbyshopify/backend-client";
import { hasChoice, selectedProductId } from "./candidates";
import { centsToInput, inputToCents } from "./scan-edit";

/** What the reviewer changed on a row before confirming it. Absent fields keep the row's own. */
export interface ReviewDraft {
  readonly price?: string;
  readonly quantity?: number;
}

/** What one row confirms with. */
export interface ReviewEntry {
  readonly group: ScanReviewGroup;
  /** The price to set; null keeps the catalogued card's own, so a confirm writes no price history. */
  readonly priceCents: number | null;
  /** Copies to stock; null stocks nothing (a customer's scan, or a card the shop does not count). */
  readonly quantity: number | null;
}

/** A customer's scan is a price question, not stock coming in. */
export const isCustomerGroup = (group: ScanReviewGroup) => group.sample.kind === "CUSTOMER";

/** The shop's own price when the card is catalogued, else the pipeline's estimate. */
export function reviewPriceText(group: ScanReviewGroup, draft: ReviewDraft | undefined): string {
  return draft?.price ?? centsToInput(group.card?.currentPriceCents ?? group.sample.estimatedPriceCents);
}

/** The group's own count until changed; zero confirms the card untracked. */
export function reviewQuantity(group: ScanReviewGroup, draft: ReviewDraft | undefined): number {
  return draft?.quantity ?? Math.max(1, group.count);
}

/** The lookup weighed products and picked none, so a confirm would price from nothing the reviewer chose. */
export function needsCandidatePick(scan: Pick<Scan, "candidates" | "scpData">): boolean {
  return scan.candidates.length > 0 && selectedProductId(scan.scpData) === null;
}

/** Look-alikes worth showing on the row. */
export function hasCandidateChoice(scan: Pick<Scan, "candidates" | "scpData">): boolean {
  return hasChoice(scan.candidates, scan.scpData);
}

/** A row confirms with a price above zero and, when the lookup left one open, a pick. */
export function reviewEntry(group: ScanReviewGroup, draft: ReviewDraft | undefined): ReviewEntry | null {
  if (needsCandidatePick(group.sample)) return null;
  const priceCents = inputToCents(reviewPriceText(group, draft));
  if (priceCents === undefined || priceCents <= 0) return null;
  const quantity = isCustomerGroup(group) ? null : reviewQuantity(group, draft) || null;
  const unchanged = group.card !== null && priceCents === group.card.currentPriceCents;
  return { group, priceCents: unchanged ? null : priceCents, quantity };
}

/**
 * The rows "Confirm all priced" takes without a look each: owner scans of a
 * card already in the catalogue, or any owner row whose price was typed.
 * New and unvetted cards and customers' price checks want the reviewer's eye.
 */
export function batchEntry(group: ScanReviewGroup, draft: ReviewDraft | undefined): ReviewEntry | null {
  if (isCustomerGroup(group)) return null;
  if (group.bucket !== "exists" && draft?.price === undefined) return null;
  return reviewEntry(group, draft);
}
